<template>
  <a
    :href="href"
    :class="['zg-page-link', {
      [`zg-page-link--background-${background}`]: background
    }]"
    tabindex="0"
    @click="$emit('tracking', trackingObject === null ? {} : trackingObject)"
  >
    <figure>
      <div class="image">
        <zg-image
          v-if="image"
          :src="image.src.default || image.src"
          :alt="image.title"
          lazy-load
        />

        <zg-illustration
          v-else-if="illustration"
          :illustration="illustration"
        />
      </div>

      <figcaption class="text">
        <h4 v-if="title">
          {{ title }}

          <zg-icon icon="forward" />
        </h4>

        <p
          v-if="text"
          v-html="text"
        />
      </figcaption>
    </figure>
  </a>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon'
  import ZgIllustration from '../ZgIllustration/ZgIllustration'
  import ZgImage from '../ZgImage/ZgImage'

  export default {
    name: 'ZgPageLink',
    components: {
      ZgIllustration,
      ZgIcon,
      ZgImage
    },
    props: {
      background: {
        type: String,
        required: false,
        default: 'beige',
        validator: value => [
          'beige',
          'white'
        ].includes(value)
      },
      image: {
        type: Object,
        required: false,
        default: null,
        validator: image => image?.src?.default || image?.src
      },
      title: {
        type: String,
        required: true
      },
      text: {
        type: String,
        required: true
      },
      href: {
        type: String,
        required: false,
        default: null
      },
      illustration: {
        type: String,
        required: false,
        default: null
      },
      trackingObject: {
        type: Object,
        required: false,
        default: null,
        validator: value => {
          return value.event && value.category && value.action && value.label
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-page-link {
    display: flex;
    flex-direction: column;
    padding: rem(24px);
    border-radius: radius(large);
    border: none;
    outline: none;
    cursor: pointer;

    .image {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-shrink: 0;
      width: rem(128px);
      height: rem(128px);
      margin: 0 0 rhythm();
      border-radius: radius(small);
      overflow: hidden;

      ::v-deep img {
        width: auto;
        height: auto;
        min-width: 100%;
        min-height: 100%;
      }

      ::v-deep svg {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }
    }

    .text {
      margin: 0;

      h4 {
        display: flex;
        flex-direction: row;
        gap: rem(8px);
        transition: all 0.3s ease;

        .zg-icon {
          flex-shrink: 0;
          margin: rem(3px) 0 0;
          transition: all 0.3s ease;
        }
      }

      ::v-deep p {
        margin: 0 0 rhythm();

        &:last-child {
          margin: 0;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-page-link {
    &:focus {
      box-shadow: box-shadow(focus);
    }

    &:focus:not(:focus-visible) {
      box-shadow: none;
    }
  }

  .zg-page-link:hover {
    .text {
      h4 {
        color: color(brand-500);

        .zg-icon {
          fill: color(brand-500);
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // background
  // ? ----------------------------------------------------------------------

  .zg-page-link--background-white {
    background-color: color(white);
  }

  .zg-page-link--background-beige {
    background-color: color(beige);
  }
</style>
